// SoftStatCardXL.jsx
import React from "react";
import { Box, Stack, Typography, Divider } from "@mui/material";
import SoftBox from "components/SoftBox";

// Minimal badge - scaling floors dropped
export function SoftBadge({ text, tone = "neutral", size = "lg", sx }) {
  const tones = {
    neutral: { bg: "rgba(0,0,0,0.06)", fg: "rgba(0,0,0,0.72)" },
    info: { bg: "rgba(99,179,237,0.18)", fg: "rgba(45,114,170,0.95)" },
    success: { bg: "rgba(72,187,120,0.16)", fg: "rgba(37,122,73,0.95)" },
    warning: { bg: "rgba(246,173,85,0.20)", fg: "rgba(140,87,20,0.95)" },
    error: { bg: "rgba(245,101,101,0.18)", fg: "rgba(150,35,35,0.95)" },
  };
  const s = size === "sm"
    ? { px: 0.6, py: 0.1, fontSize: "clamp(8px, 1vh, 11px)" }
    : { px: 0.8, py: 0.2, fontSize: "clamp(9px, 1.1vh, 12px)" };
  const t = tones[tone] || tones.neutral;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "999px",
        background: t.bg,
        color: t.fg,
        fontWeight: 900,
        ...s,
        ...sx,
      }}
    >
      {text}
    </Box>
  );
}

function fmtDelta(delta, suffix = "") {
  if (delta == null) return "";
  if (typeof delta === "number") {
    const sign = delta > 0 ? "+" : "";
    const v = Math.abs(delta) >= 100 ? delta.toFixed(0) : delta.toFixed(1);
    return `${sign}${v}${suffix}`;
  }
  return String(delta);
}

function toneFromDelta(delta) {
  if (typeof delta !== "number") return "neutral";
  if (delta > 0) return "success";
  if (delta < 0) return "error";
  return "neutral";
}

export function SoftStatCardXL({
  title,
  value,
  unit,
  ingress,
  egress,
  status,
  delta,
  deltaSuffix,
  deltaTone,
  comparisons = [],
  compareTitle = "Compared to",
  compareLayout = "grid",
  formatCompareValue,
  showCompareDelta = true,
  footerLeft,
  footerRight,
  icon,
  sx
}) {
  const isNum = typeof delta === "number";
  const autoTone = deltaTone || (isNum ? toneFromDelta(delta) : "neutral");

  const compareContainerSx =
    compareLayout === "stack"
      ? { flexDirection: "column", gap: 0.4 }
      : compareLayout === "grid"
        ? { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0.5 }
        : { flexDirection: "row", flexWrap: "wrap", gap: 0.5 };

  const CompareItem = ({ item }) => {
    const prevText = formatCompareValue ? formatCompareValue(item.prevValue, item) : String(item.prevValue ?? "—");
    const tone = item.tone || toneFromDelta(item.delta);

    return (
      <SoftBox
        sx={{
          borderRadius: "8px",
          px: 0.75,
          py: "clamp(4px, 0.8vh, 12px)",
          display: "flex",
          flexDirection: "column",
          gap: "clamp(1px, 0.4vh, 6px)",
          background: "rgba(0,0,0,0.03)",
          border: "1px solid rgba(255,255,255,0.4)",
          minWidth: compareLayout === "grid" ? 0 : 120,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography sx={{ fontSize: "clamp(8px, 1vh, 11px)", fontWeight: 900, opacity: 0.7 }}>
            {item.label}
          </Typography>
          {showCompareDelta && item.delta != null && (
            <SoftBadge text={fmtDelta(item.delta, item.deltaSuffix ?? "")} tone={tone} size="sm" />
          )}
        </Stack>
        <Typography sx={{ fontSize: "clamp(10px, 1.4vh, 14px)", fontWeight: 900, lineHeight: 1.1 }}>
          {prevText}
        </Typography>
      </SoftBox>
    );
  };

  return (
    <SoftBox
      sx={{
        borderRadius: "18px",
        px: 2,
        py: "clamp(4px, 1.5vh, 20px)",
        minWidth: "clamp(200px, 20vw, 340px)",
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.6)",
        display: "flex",
        flexDirection: "column",
        height: "100%", // Ensures the card stretches to fill parent height
        gap: "clamp(4px, 1vh, 14px)",
        alignSelf: "start",
        ...sx,
      }}
    >
      {/* --- TOP SECTION --- */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            {icon && (
              <Box
                sx={{
                  width: "clamp(24px, 3.5vh, 40px)",
                  height: "clamp(24px, 3.5vh, 40px)",
                  borderRadius: "8px",
                  display: "grid",
                  placeItems: "center",
                  background: "rgba(0,0,0,0.05)",
                }}
              >
                {icon}
              </Box>
            )}
            <Typography sx={{ fontSize: "clamp(9px, 2vh, 30px)", fontWeight: 900, opacity: 0.75 }}>
              {title}
            </Typography>
          </Stack>
          {status && <SoftBadge text={status.text} tone={status.tone} size="lg" />}
        </Stack>

        <Stack direction="row" alignItems="baseline" spacing={0.8}>
          <Typography
            sx={{
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: "-0.5px",
              fontSize: "clamp(16px, 4vh, 48px)",
            }}
          >
            {value}
          </Typography>
          {unit && (
            <Typography sx={{ fontSize: "clamp(10px, 1.4vh, 14px)", fontWeight: 800, opacity: 0.7 }}>
              {unit}
            </Typography>
          )}
        </Stack>

        {typeof ingress != 'undefined' && typeof egress != 'undefined' && <>
          <Typography display="inline" sx={{ mt: 0.5, fontSize: "clamp(20px, 2vh, 32px)", opacity: 0.7, fontWeight: 700, lineHeight: 1.1}}>
            {ingress.toLocaleString()} <span style={{'fontSize': 'clamp(15px, 1.5vh, 25px)'}}>Ingress / </span>
          </Typography>
          <Typography display="inline" sx={{ mt: 0.5, fontSize: "clamp(20px, 2vh, 32px)", opacity: 0.7, fontWeight: 700, lineHeight: 1.1}}>
            {egress.toLocaleString()} <span style={{'fontSize': 'clamp(15px, 1.5vh, 25px)'}}>Egress</span>
          </Typography>
        </> 
        }
      </Box>

      {/* --- SPACER --- */}
      {/* This pushes everything below it to the bottom of the card */}
      <Box sx={{ flexGrow: 1 }} />

      {/* --- BOTTOM SECTION --- */}
      <SoftBox>
        {comparisons?.length > 0 && (
          <>
            <Stack spacing={0.5}>
              <Typography sx={{ fontSize: "clamp(8px, 1vh, 11px)", fontWeight: 900, opacity: 0.7 }}>
                {compareTitle}
              </Typography>
              <Box sx={compareContainerSx}>
                {comparisons.map((c) => (
                  <CompareItem key={c.key || c.label} item={c} />
                ))}
              </Box>
            </Stack>
          </>
        )}

        {(footerLeft || footerRight) && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mt: 1,
              pt: "clamp(4px, 0.7vh, 8px)",
              borderTop: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Typography sx={{ fontSize: "clamp(8px, 1vh, 10.5px)", opacity: 0.65, fontWeight: 700 }}>
              {footerLeft}
            </Typography>
            <Typography sx={{ fontSize: "clamp(8px, 1vh, 10.5px)", opacity: 0.65, fontWeight: 700 }}>
              {footerRight}
            </Typography>
          </Stack>
        )}
        <Divider m={0}></Divider>
      </SoftBox>
    </SoftBox>
  );
}